import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Yup from 'yup';
import { Field, Form, Formik } from 'formik';
import {
  getActivePathStringWithSpace,
} from 'documents/store/folders/selectors';
import { setActiveDocumentId } from 'documents/store/documents/actions';
import { getActiveDocument } from 'documents/store/documents/selectors';
import { closeModal } from 'modal/store/actions';
import Title3 from 'base/components/Title3';
import { FileIcon } from 'base/components/Icon';
import MainTitle from 'base/components/MainTitle';
import FieldWrapper from 'base/components/forms/FieldWrapper';
import FormikInputField from 'base/components/forms/formik/FormikInputField';
import SubmitButton from 'base/components/forms/SubmitButton';
import Spinner from 'base/components/Spinner';
import ModalHeader from 'modal/components/ModalHeader';
import { getOfflineWaitingForSyncIds, getOfflineSyncErrorIds } from '../../store/selectors';
import OfflineAnnotationStore from '../../util/OfflineAnnotationStore';
import api from '../../../api';
import theme from '../../../theme';
import { getEntities } from '../../../store/entities/selectors';
import { cacheDocumentOffline, removeOfflineWaitingForSyncId, addOfflineSyncErrorId } from '../../store/actions';

class OfflineChangesSyncModal extends React.Component {
  state = {
    ids: this.props.ids,
    idsWithPassword: [],
  };

  componentDidMount() {
    this.syncDocuments();
  }

  syncDocuments = () => {
    const { ids } = this.state;
    ids.forEach((id) => this.syncDocument(id));
  };

  syncDocument = (id, password = null) => {
    const {
      removeOfflineWaitingForSyncId, addOfflineSyncErrorId, offlineState,
    } = this.props;
    OfflineAnnotationStore.get(id)
      .then((data) => {
        if (!data || !data.annotations) {
          throw new Error(`Annotations not found in OfflineAnnotationStore for id ${id}`);
        }
        api.documents.updateAnnotations(id, data.annotations, password).toPromise()
          .then((response) => {
            this.setState((prevState) => ({
              ids: prevState.ids.filter((i) => i !== id),
              idsWithPassword: prevState.idsWithPassword.filter((i) => i !== id),
            }));
            removeOfflineWaitingForSyncId(response.body);
          });
      })
      .catch((error) => {
        // Use console to log to logrocket since we cannot pass custom objects otherwise
          console.error(`Offline sync error: ${error}`, { // eslint-disable-line
          offlineState,
          componentState: this.state,
        });
        if (error.status && error.status === 400) {
          this.setState((prevState) => ({
            idsWithPassword: [...prevState.idsWithPassword, id],
          }));
        } else {
          this.setState((prevState) => ({
            ids: prevState.ids.filter((i) => i !== id),
            idsWithPassword: prevState.idsWithPassword.filter((i) => i !== id),
          }));
          addOfflineSyncErrorId(id);
          OfflineAnnotationStore.keys().then((keys) =>
            Promise.all(keys.map((key) => OfflineAnnotationStore.get(key)))
              .then((values) => {
                const annotationsById = keys
                  .reduce((obj, k, i) => ({ ...obj, [k]: values[i] }), {});
                // Use console to log to logrocket since we cannot pass custom objects otherwise
                console.error('Offline sync annotations store', annotationsById); // eslint-disable-line
              }));
        }
      });
  };

  handlePasswordSubmit = (values) => {
    this.syncDocument(values.id, values.password);
  };

  render() {
    const {
      name, closeModal, activeDocument, setActiveDocumentId, syncErrorIds // eslint-disable-line
    } = this.props;
    const { ids, idsWithPassword } = this.state;

    const isDone = ids.length === 0 && idsWithPassword.length === 0;
    if (isDone) {
      setTimeout(() => closeModal(name), 3000);
    }

    const nextIdWithPassword = idsWithPassword.length > 0 ? idsWithPassword[0] : null;
    if (nextIdWithPassword) {
      setActiveDocumentId(nextIdWithPassword);
    }

    const spinner = (
      <FieldWrapper>
          This may take a moment.
        <br />
        {' '}
        <br />
        <Spinner color={theme.colors.light} />
      </FieldWrapper>
    );

    const done = (
      <FieldWrapper>
        <Title3><strong>Synchronization complete</strong></Title3>
        {syncErrorIds.length > 0 && (
          <Title3>
            <br />
            Some documents could not be synchronized.
            Please contact an administrator.
          </Title3>
        )}
      </FieldWrapper>
    );

    return (
      <>
        <ModalHeader>
          <MainTitle>
              Synchronizing offline changes with server
            <br />
          </MainTitle>
        </ModalHeader>

        {isDone ? done : spinner}

        {nextIdWithPassword && (
        <Formik
          initialValues={{
            password: '',
            id: nextIdWithPassword,
          }}
          validationSchema={
            Yup.object().shape({
              password: Yup.string().required('PDF password is required.'),
            })
          }
          render={({errors, isSubmitting}) => ( // eslint-disable-line
            <Form>
              <FieldWrapper>
                <Title3><strong>Password required</strong></Title3>
              </FieldWrapper>
              <FieldWrapper>
                You have to unlock the following document in order to save your offline changes:
              </FieldWrapper>
              <FieldWrapper>
                <Title3>
                  <FileIcon />
                  {' '}
                  {activeDocument && activeDocument.name}
                </Title3>
              </FieldWrapper>
              <Field
                type="password"
                name="password"
                placeholder="PDF password"
                component={FormikInputField}
                autoFocus
              />
              <SubmitButton type="submit">
                  Verify
              </SubmitButton>
            </Form>
          )}
          onSubmit={this.handlePasswordSubmit}
        />
        )}
      </>
    );
  }
}
OfflineChangesSyncModal.propTypes = {
  name: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  activeDocument: PropTypes.objectOf(PropTypes.any).isRequired,
  removeOfflineWaitingForSyncId: PropTypes.func.isRequired,
  addOfflineSyncErrorId: PropTypes.func.isRequired,
  ids: PropTypes.arrayOf(PropTypes.any),
  setActiveDocumentId: PropTypes.func.isRequired,
  syncErrorIds: PropTypes.arrayOf(PropTypes.any),
  offlineState: PropTypes.objectOf(PropTypes.any),
};
OfflineChangesSyncModal.defaultProps = {
  ids: [],
  syncErrorIds: [],
  offlineState: {},
};

const mapStateToProps = (state) => ({
  activePathString: getActivePathStringWithSpace(state),
  entities: getEntities(state),
  activeDocument: getActiveDocument(state),
  ids: getOfflineWaitingForSyncIds(state),
  syncErrorIds: getOfflineSyncErrorIds(state),
  offlineState: state.offline,
});

const mapDispatchToProps = {
  cacheDocumentOffline,
  closeModal,
  removeOfflineWaitingForSyncId,
  setActiveDocumentId,
  addOfflineSyncErrorId,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(OfflineChangesSyncModal);
